import { useEffect, useRef } from "react";
import type { TransferJobEvent } from "@/features/employeeTransfer/types/employeeTransfer.types";
import { usePush } from "@/shared/hooks/usePush";

const CHANNEL_PREFIX = "employee-transfer-";
const EVENT_NAME = "job_updated";
// Backend job processing SLA — fail if no terminal Pusher event within 2 minutes
const TIMEOUT_MS = 120_000;

const VALID_STATUSES = new Set(["starting", "in_progress", "processed", "failed"]);

const isValidTransferEvent = (data: unknown): data is TransferJobEvent =>
  data !== null
  && typeof data === "object"
  && typeof (data as TransferJobEvent).status === "string"
  && VALID_STATUSES.has((data as TransferJobEvent).status)
  && typeof (data as TransferJobEvent).percent === "number"
  && (data as TransferJobEvent).percent >= 0
  && (data as TransferJobEvent).percent <= 100;

type UseTransferProgressOptions = {
  token: string | null
  onComplete: () => void
  onFailed: () => void
  onProgress: (percent: number) => void
};

export const useTransferProgress = ({
  token,
  onComplete,
  onFailed,
  onProgress,
}: UseTransferProgressOptions): void => {
  const { subscribe, unsubscribe } = usePush();
  const terminalReceivedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const onFailedRef = useRef(onFailed);
  const onProgressRef = useRef(onProgress);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onFailedRef.current = onFailed;
    onProgressRef.current = onProgress;
  });

  useEffect(() => {
    if (!token) return;

    const channel = `${CHANNEL_PREFIX}${token}`;
    terminalReceivedRef.current = false;

    const handleEvent = (data: unknown) => {
      if (terminalReceivedRef.current) return;
      if (!isValidTransferEvent(data)) return;

      if (data.status === "processed") {
        terminalReceivedRef.current = true;
        onCompleteRef.current();
      }
      else if (data.status === "failed") {
        terminalReceivedRef.current = true;
        onFailedRef.current();
      }
      else {
        onProgressRef.current(data.percent);
      }
    };

    subscribe(channel, EVENT_NAME, handleEvent);

    const timeoutId = setTimeout(() => {
      if (!terminalReceivedRef.current) {
        onFailedRef.current();
      }
    }, TIMEOUT_MS);

    return () => {
      unsubscribe(channel, EVENT_NAME, handleEvent);
      clearTimeout(timeoutId);
    };
  }, [token, subscribe, unsubscribe]);
};
